import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { BadgeComponent } from '../badge/badge';
import { SkeletonLoaderComponent } from '../skeleton-loader/skeleton-loader.component';
import { AudioPlayerModalComponent } from '../audio-player-modal/audio-player-modal.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { StatsCardComponent } from '../stats-card/stats-card';
import { AuthService } from '../../../core/services/auth/auth.service';
import { UserRole } from '../../../core/models/role.enum';

// Interfaces
export interface CallLog {
  id: string;
  callerId: string;
  patientName: string;
  telecallerName: string;
  date: string;
  startTime: string;
  endTime: string;
  duration: string;
  nextFollowUp: string;
  callType: 'Incoming' | 'Outgoing';
  outcome: 'COMPLETED' | 'RESCHEDULED' | 'NO_ANSWER' | 'CANCELLED' | 'FAILED' | 'BUSY';
  program?: string;
  recordingUrl?: string;
  // Additional fields
  patientId?: string;
  patientDocumentId?: string;
  patientImage?: string | null;
  userId?: string; // Telecaller Display ID
  telecallerDocumentId?: string;
  telecallerImage?: string | null;
}

export interface CallStats {
  totalCalls: number;
  totalCallDuration: string;
  avgCallDuration: string;
  scheduledCalls: number;
}

export interface FilterOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-call-logs-view',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, BadgeComponent, SkeletonLoaderComponent, AudioPlayerModalComponent, PaginationComponent, StatsCardComponent],
  templateUrl: './call-logs-view.component.html',
  styleUrls: ['./call-logs-view.component.css']
})
export class CallLogsViewComponent implements OnInit, OnChanges {
  private authService = inject(AuthService);

  @Input() callLogs: CallLog[] = [];
  @Input() stats: CallStats = {
    totalCalls: 0,
    totalCallDuration: '0h 0m',
    avgCallDuration: '0m',
    scheduledCalls: 0
  };
  @Input() isLoading: boolean = false;
  isInitialLoad: boolean = true; // Track if it's the initial load
  
  // Filter Options Inputs
  @Input() telecallerOptions: FilterOption[] = [];
  @Input() showTelecallerFilter: boolean | null = null;
  @Input() dateRangeOptions: FilterOption[] = [
    { id: 'today', name: 'Today' },
    { id: 'yesterday', name: 'Yesterday' },
    { id: 'last7days', name: 'Last 7 Days' },
    { id: 'last30days', name: 'Last 30 Days' },
    { id: 'thisMonth', name: 'This Month' },
    { id: 'lastMonth', name: 'Last Month' }
  ];
  @Input() programOptions: FilterOption[] = []; // Optional

  // Configuration
  @Input() serverSideDateFiltering: boolean = false;

  /**
   * When true the parent fetches one page at a time from the API, so this component
   * renders `callLogs` as-is and asks the parent for a new page instead of slicing locally.
   */
  @Input() serverSidePagination: boolean = false;
  /** Total number of records on the server (from the API pagination metadata). */
  @Input() totalRecords: number = 0;
  /** Page size owned by the parent when paginating server-side. */
  @Input() pageSize: number = 10; 

  get isTelecallerFilterVisible(): boolean {
    if (this.showTelecallerFilter !== null) {
      return this.showTelecallerFilter;
    }
    const user = this.authService.currentUser();
    if (user?.role === UserRole.TELECALLER) {
      return false;
    }
    return this.telecallerOptions.length > 0;
  } 

  // Output Events for Filter Changes
  @Output() dateRangeChange = new EventEmitter<FilterOption[]>();
  @Output() telecallerChange = new EventEmitter<FilterOption[]>();
  @Output() refresh = new EventEmitter<void>();
  /** Emitted in server-side pagination mode whenever the user changes page or page size. */
  @Output() pageChange = new EventEmitter<{ page: number; pageSize: number }>();

  // Internal State
  searchTerm: string = '';
  selectedDateRanges: FilterOption | null = null; // Changed from array to single object
  selectedTelecallers: FilterOption[] = [];
  selectedPrograms: FilterOption[] = [];

  filteredCallLogs: CallLog[] = [];
  
  // Pagination
  currentPage: number = 1;
  rowsPerPage: number = 10;
  totalPages: number = 1;
  paginationSizes: number[] = [5, 10, 20, 50];

  // Audio Player
  showAudioModal: boolean = false;
  selectedRecordingUrl: string = '';
  selectedCallInfo: { patientName: string; date: string; duration: string } | null = null;

  ngOnInit(): void {
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageSize'] && this.pageSize) {
      this.rowsPerPage = this.pageSize;
    }

    if (changes['totalRecords']) {
      this.recalculateTotalPages();
    }

    if (changes['callLogs']) {
      this.processData();
      if (this.callLogs && this.callLogs.length > 0) {
        this.isInitialLoad = false; // Set to false after first data load
      }
    }
    
    // Also set to false when loading completes (even if no data)
    if (changes['isLoading'] && !changes['isLoading'].currentValue && changes['isLoading'].previousValue) {
      this.isInitialLoad = false;
    }
  }

  processData(): void {
    this.applyFilters();
  }

  // Compare function for ng-select
  compareFn(item1: any, item2: any): boolean {
    return item1 && item2 ? item1.id === item2.id : item1 === item2;
  }

  // Handle Filters Change (Called by UI)
  onFiltersChange(): void {
    // Emit events for parents that handle filtering server-side
    // We always emit the change events
    if (this.selectedDateRanges) {
        this.dateRangeChange.emit([this.selectedDateRanges]); // Wrap in array for backward compatibility
    } else {
        this.dateRangeChange.emit([]);
    }
    
    // For Telecaller changes, we might want to filter locally or server side
    this.telecallerChange.emit(this.selectedTelecallers);

    // A new filter set always restarts at the first page
    this.currentPage = 1;

    // Apply local filters (Search, and Telecaller/Date if not server-side)
    this.applyFilters();
  }

  applyFilters(): void {
    if (!this.callLogs) return;

    this.filteredCallLogs = this.callLogs.filter(log => {
      // 1. Search Filter (Always Client Side)
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        const matchesSearch = 
          log.patientName.toLowerCase().includes(searchLower) ||
          log.telecallerName.toLowerCase().includes(searchLower) ||
          log.callerId.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // 2. Telecaller Filter
      // Usually Client Side unless parent specifically handles it by creating a new `callLogs` input.
      // We will apply it here. If the parent sends a pre-filtered list (single telecaller), this check passes anyway on the matches.
      // But for Organization view (Server sends all logs), we need this.
      if (this.selectedTelecallers.length > 0) {
        const matchesTelecaller = this.selectedTelecallers.some(
          t => t.name === log.telecallerName || t.id === log.userId // Check ID if possible
        );
        // Note: `log.telecallerName` is reliable. `userId` might be missing or different format.
        // Fallback to name check strictly if that's what we used before.
        const matchesName = this.selectedTelecallers.some(t => t.name === log.telecallerName);
        if (!matchesName) return false;
      }

      // 3. Program Filter
      if (this.selectedPrograms.length > 0) {
        const matchesProgram = this.selectedPrograms.some(
          p => p.name === log.program
        );
        if (!matchesProgram) return false;
      }

      // 4. Date Range Filter
      // If serverSideDateFiltering is true, we assume `callLogs` is ALREADY filtered by the parent (via API).
      // If false, we check here.
      if (!this.serverSideDateFiltering && this.selectedDateRanges) {
        if (!this.checkDateRange(log.date)) return false;
      }

      return true;
    });

    this.recalculateTotalPages();
  }

  /**
   * Page count comes from the server total when paginating server-side, and from the
   * locally filtered rows otherwise.
   */
  private recalculateTotalPages(): void {
    this.totalPages = Math.max(1, Math.ceil(this.paginationTotalItems / this.rowsPerPage));
    // Reset to page 1 if current page out of bounds
    if (this.currentPage > this.totalPages) this.currentPage = 1;
    if (this.currentPage < 1) this.currentPage = 1;
  }

  checkDateRange(logDateStr: string): boolean {
    // logDateStr is in "DD/MM/YYYY" format
    if (!logDateStr || !this.selectedDateRanges) return false;
    const parts = logDateStr.split('/');
    if (parts.length !== 3) return false;
    
    const [day, month, year] = parts.map(Number);
    const logDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today to start of day

    const range = this.selectedDateRanges;
    switch (range.id) {
      case 'today':
        return logDate.getTime() === today.getTime();
      case 'yesterday':
         const yesterday = new Date(today);
         yesterday.setDate(today.getDate() - 1);
         return logDate.getTime() === yesterday.getTime();
      case 'last7days':
         const last7Days = new Date(today);
         last7Days.setDate(today.getDate() - 7);
         return logDate >= last7Days && logDate <= today;
      case 'last30days':
         const last30Days = new Date(today);
         last30Days.setDate(today.getDate() - 30);
         return logDate >= last30Days && logDate <= today;
      case 'thisMonth':
         return logDate.getMonth() === today.getMonth() && logDate.getFullYear() === today.getFullYear();
      case 'lastMonth':
         const lastMonth = new Date(today);
         lastMonth.setMonth(today.getMonth() - 1);
         return logDate.getMonth() === lastMonth.getMonth() && logDate.getFullYear() === lastMonth.getFullYear();
      default:
         return true;
    }
  }

  // Remove individual filter
  removeFilter(type: 'dateRange' | 'telecaller' | 'program', item: FilterOption): void {
    if (type === 'dateRange') {
      this.selectedDateRanges = null; // Clear the single selection
      this.dateRangeChange.emit([]);
    } else if (type === 'telecaller') {
      this.selectedTelecallers = this.selectedTelecallers.filter(t => t.id !== item.id);
      this.telecallerChange.emit(this.selectedTelecallers);
    } else if (type === 'program') {
      this.selectedPrograms = this.selectedPrograms.filter(p => p.id !== item.id);
    }
    this.applyFilters();
  }

  // Clear all filters
  clearAllFilters(): void {
    this.selectedDateRanges = null; // Clear the single selection
    this.selectedTelecallers = [];
    this.selectedPrograms = [];
    this.searchTerm = '';
    
    this.dateRangeChange.emit([]);
    this.telecallerChange.emit([]);
    
    this.applyFilters();
  }

  // Pagination Accessors
  get paginatedCallLogs(): CallLog[] {
    // Server-side: `callLogs` already is the current page, so never slice it again.
    if (this.serverSidePagination) {
      return this.filteredCallLogs;
    }
    const start = (this.currentPage - 1) * this.rowsPerPage;
    const end = start + this.rowsPerPage;
    return this.filteredCallLogs.slice(start, end);
  }

  get paginationTotalItems(): number {
    return this.serverSidePagination ? this.totalRecords : this.filteredCallLogs.length;
  }

  get paginationStart(): number {
    return this.paginationTotalItems === 0 ? 0 : (this.currentPage - 1) * this.rowsPerPage + 1;
  }

  get paginationEnd(): number {
    return Math.min(this.currentPage * this.rowsPerPage, this.paginationTotalItems);
  }

  get isEmpty(): boolean {
    return this.filteredCallLogs.length === 0;
  }

  get pages(): number[] {
    const maxPages = 5;
    const pages: number[] = [];
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  onPageChange(newPage: number): void {
    if (newPage === this.currentPage) return;
    this.currentPage = newPage;
    this.emitPageChange();
  }

  onPageSizeChange(newPageSize: number): void {
    this.rowsPerPage = newPageSize;
    this.currentPage = 1;
    this.recalculateTotalPages();
    this.emitPageChange();
  }

  goToPage(pageNumber: number): void {
    this.onPageChange(pageNumber);
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.onPageChange(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.onPageChange(this.currentPage + 1);
    }
  }

  onRowsPerPageChange(): void {
    this.onPageSizeChange(this.rowsPerPage);
  }

  /** Asks the parent for a fresh page; no-op when pagination is handled locally. */
  private emitPageChange(): void {
    if (this.serverSidePagination) {
      this.pageChange.emit({ page: this.currentPage, pageSize: this.rowsPerPage });
    }
  }

  // Badge Variant
  getOutcomeVariant(outcome: string): 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' {
    switch (outcome) {
      case 'COMPLETED':
        return 'success';
      case 'BUSY':
      case 'NO_ANSWER':
        return 'warning';
      case 'FAILED':
      case 'CANCELLED':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  // Audio Player
  openAudioModal(callLog: CallLog): void {
    if (!callLog.recordingUrl) return;

    this.selectedRecordingUrl = callLog.recordingUrl;
    this.selectedCallInfo = {
      patientName: callLog.patientName,
      date: callLog.date,
      duration: callLog.duration
    };
    this.showAudioModal = true;
  }

  closeAudioModal(): void {
    this.showAudioModal = false;
    this.selectedRecordingUrl = '';
    this.selectedCallInfo = null;
  }
}
